import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { InfoscreenHostComponent } from '../infoscreen-smart/infoscreen-host.component';
import { ConfigService } from '../services/config.service';
import { InfoscreenModuleService } from './infoscreen-module.service';
import { PagePieComponent } from '../school-calendar/page-pie.component';
import { ScreenHostComponent } from './screen-host/screen-host.component';

type Mode = 'deciding' | 'legacy' | 'screen' | 'rotate';

/** How long a rotating demo (`?screenIds=`) shows each screen. */
export const ROTATE_SECONDS = 30;

/** How often a screen on the old address asks whether a module screen has taken it over. */
const LEGACY_RECHECK_MS = 5 * 60 * 1000;

/**
 * Chooses between the old screen and the infoscreen module, and nothing else.
 *
 * - `?screenId=12` → the module's screen 12.
 * - `?screenIds=12,14,15` → a demo going through those screens, `ROTATE_SECONDS` each, by the clock
 *   (so two such screens change together); a screen that is gone drops out of the round.
 * - `?locationId=4` → the module's screen when one has taken over that address
 *   (`ReplacesLegacyUrl`) and the tenant has the module; otherwise the old screen, unchanged.
 *   Any error on the way also ends at the old screen, so a wall screen never goes blank because
 *   of the module.
 *
 * Screens on the wall are never reloaded by hand, so on the old address the choice is made again
 * while running: every few minutes in the old view, and at once when the module's screen goes away
 * (deleted, switched off, address released, module off).
 */
@Component({
  selector: 'app-infoscreen-entry',
  imports: [InfoscreenHostComponent, PagePieComponent, ScreenHostComponent],
  template: `
    @switch (mode()) {
      @case ('screen') {
        <app-screen-host [screenId]="screenId()!" (gone)="onScreenGone()" />
      }
      @case ('rotate') {
        @if (rotation(); as turn) {
          <!-- One screen at a time, made afresh on each turn, so it starts as it does on the wall. -->
          @for (id of [turn.id]; track id) {
            <app-screen-host [screenId]="id" [demoTurn]="turn.round" (gone)="onRotatingGone(id)" />
          }
          <div class="rotate-badge">
            Demo · skærm {{ turn.index + 1 }}/{{ turn.count }}
            <app-page-pie [index]="turn.index" [count]="turn.count" [seconds]="rotateSeconds" [elapsed]="turn.elapsed" />
          </div>
        }
      }
      @case ('legacy') {
        <app-infoscreen-host />
      }
    }
  `,
  styles: `
    /* Small at the bottom in the middle, over the screen and clear of a demo screen's own badge at the top. */
    .rotate-badge {
      position: fixed;
      bottom: 6vh;
      left: 50%;
      transform: translateX(-50%);
      z-index: 30;
      display: flex;
      align-items: center;
      gap: 0.6em;
      padding: 0.25em 0.9em;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.55);
      color: #ffffff;
      font-size: max(14px, 1.6vh);
      font-weight: 600;
      white-space: nowrap;
      pointer-events: none;
      --ink-rgb: 255, 255, 255;
    }
  `,
})
export class InfoscreenEntryComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly moduleService = inject(InfoscreenModuleService);
  private readonly configService = inject(ConfigService);

  readonly mode = signal<Mode>('deciding');
  readonly screenId = signal<number | null>(null);

  /** A rotating demo: the screens it goes through, and the turn it is on (by the clock). */
  readonly rotateIds = signal<number[]>([]);
  private readonly turn = signal(0);
  readonly rotateSeconds = ROTATE_SECONDS;
  readonly rotation = computed(() => {
    const ids = this.rotateIds();
    if (!ids.length) return null;
    const index = this.turn() % ids.length;
    return {
      id: ids[index],
      index,
      count: ids.length,
      // The rounds so far: a screen in the round that is a theme demo shows its next set-up each round.
      round: Math.floor(this.turn() / ids.length),
      elapsed: (Date.now() % (ROTATE_SECONDS * 1000)) / 1000,
    };
  });
  private turnId?: ReturnType<typeof setTimeout>;

  /** Set when the page was opened on the old address — only then may the choice change. */
  private locationId: number | null = null;
  private recheckId?: ReturnType<typeof setInterval>;

  async ngOnInit() {
    const params = this.route.snapshot.queryParams;
    const screenId = parseInt(params['screenId'], 10);
    const locationId = parseInt(params['locationId'], 10);
    const screenIds = String(params['screenIds'] ?? '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id, i, all) => id > 0 && all.indexOf(id) === i);

    if (screenIds.length) {
      this.rotateIds.set(screenIds);
      this.mode.set('rotate');
      this.nextTurn();
      return;
    }

    if (screenId > 0) {
      this.showScreen(screenId);
      return;
    }

    if (locationId > 0 && !this.configService.isUsingMockApi()) {
      this.locationId = locationId;
      this.recheckId = setInterval(() => void this.recheck(), LEGACY_RECHECK_MS);
      if (await this.recheck()) return;
    }

    this.mode.set('legacy');
  }

  ngOnDestroy() {
    clearInterval(this.recheckId);
    clearTimeout(this.turnId);
  }

  /** The turn by the clock, and a timer for the next one, on the second it is due. */
  private nextTurn() {
    const ms = ROTATE_SECONDS * 1000;
    this.turn.set(Math.floor(Date.now() / ms));
    this.turnId = setTimeout(() => this.nextTurn(), ms - (Date.now() % ms));
  }

  /** A screen in the round answered 403/404: it drops out, and the round goes on without it. */
  onRotatingGone(id: number) {
    this.rotateIds.update((ids) => ids.filter((other) => other !== id));
  }

  /** The module's screen answered 403/404. On the old address that means: back to the old view. */
  onScreenGone() {
    if (this.locationId) {
      this.screenId.set(null);
      this.mode.set('legacy');
    }
  }

  /** Switches to a screen that has taken over the old address. True when it did. */
  private async recheck(): Promise<boolean> {
    if (!this.locationId || this.mode() === 'screen') return false;
    try {
      const takenOver = await firstValueFrom(this.moduleService.getLegacyScreenId(this.locationId));
      if (takenOver) {
        this.showScreen(takenOver);
        return true;
      }
    } catch {
      // Old API, module tables missing, network — the old screen is the safe answer.
    }
    return false;
  }

  private showScreen(id: number) {
    this.screenId.set(id);
    this.mode.set('screen');
  }
}
